import { useSearchParams } from 'react-router-dom';
import { CampoDeSelecao } from '../../componentes/ui';
import { Pagina } from '../Carteira';
import { ConfiguracoesDoCrm } from '../crm/ConfiguracoesDoCrm';
import { Automacoes } from './Automacoes';
import { BarraDeAbas, type AbaDeConfiguracao } from './BarraDeAbas';
import { abasDoPassivo } from './ConfiguracoesPassivo';
import { abasDoPontual } from './ConfiguracoesPontual';
import { abasDoRecorrente } from './ConfiguracoesRecorrente';
import { ModelosDeContrato } from './ModelosDeContrato';
import { ModelosDeEmail } from './ModelosDeEmail';

/** Os serviços do dropdown, na ordem do menu. */
const SERVICOS: readonly {
  readonly id: string;
  readonly rotulo: string;
  readonly abas: () => AbaDeConfiguracao[];
}[] = [
  { id: 'recorrente', rotulo: 'Créditos ICMS Recorrente', abas: abasDoRecorrente },
  { id: 'passivo', rotulo: 'Créditos ICMS Passivos', abas: abasDoPassivo },
  { id: 'pontual', rotulo: 'Créditos ICMS Pontual', abas: abasDoPontual },
  {
    id: 'crm',
    rotulo: 'CRM',
    abas: () => [{ id: 'pipeline', rotulo: 'Pipeline', conteudo: <ConfiguracoesDoCrm /> }],
  },
];

const ABAS = [
  { id: 'servicos', rotulo: 'Serviços' },
  { id: 'automacoes', rotulo: 'Automações' },
  { id: 'emails', rotulo: 'E-mails' },
  { id: 'contratos', rotulo: 'Contratos' },
] as const;

/**
 * Central de Configurações — o que antes era um item "Configurações" em cada
 * divisão do menu, num lugar só, mais as automações, os e-mails (D-017) e os
 * modelos gerais de contrato (D-020).
 *
 * A aba, o serviço e a seção ficam no endereço (`?aba=`, `?servico=`,
 * `?secao=`): os caminhos antigos de cada divisão redirecionam para cá já no
 * serviço certo, e um link para "as automações" abre nelas.
 */
export function CentralDeConfiguracoes(): JSX.Element {
  const [parametros, setParametros] = useSearchParams();
  const aba = ABAS.find((a) => a.id === parametros.get('aba'))?.id ?? 'servicos';
  const servico = SERVICOS.find((s) => s.id === parametros.get('servico')) ?? SERVICOS[0]!;
  const secoes = servico.abas();
  const secao = secoes.find((s) => s.id === parametros.get('secao')) ?? secoes[0];

  const ir = (mudancas: Record<string, string | null>): void => {
    const proximos = new URLSearchParams(parametros);
    for (const [chave, valor] of Object.entries(mudancas)) {
      if (valor === null) proximos.delete(chave);
      else proximos.set(chave, valor);
    }
    setParametros(proximos, { replace: true });
  };

  return (
    <Pagina
      titulo="Configurações"
      descricao="Os parâmetros de cada serviço, as automações que o Deca faz sozinho, o texto dos e-mails e os modelos de contrato."
    >
      <BarraDeAbas
        rotulo="Seções das configurações"
        abas={ABAS}
        atual={aba}
        aoTrocar={(id) => ir({ aba: id === 'servicos' ? null : id, email: null })}
      />

      {aba === 'automacoes' ? (
        <Automacoes />
      ) : aba === 'emails' ? (
        <ModelosDeEmail />
      ) : aba === 'contratos' ? (
        <ModelosDeContrato />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <CampoDeSelecao
              rotulo="Serviço"
              className="sm:w-80"
              value={servico.id}
              onChange={(e) => ir({ servico: e.target.value, secao: null })}
              opcoes={SERVICOS.map((s) => ({ valor: s.id, rotulo: s.rotulo }))}
            />
            {/* Com uma seção só, o botão não troca nada: o título do conteúdo já nomeia. */}
            {secoes.length > 1 && (
              <BarraDeAbas
                rotulo={`Configurações de ${servico.rotulo}`}
                abas={secoes}
                atual={secao?.id ?? ''}
                aoTrocar={(id) => ir({ secao: id })}
              />
            )}
          </div>
          {secao?.conteudo}
        </div>
      )}
    </Pagina>
  );
}
